import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../common/Navbar';
import { 
  Bell,
  RefreshCw,
  Check,
  CheckCheck,
  Calendar,
  User,
  Building,
  AlertTriangle
} from 'lucide-react';
import { notificationService } from '../../services/api';

export default function NotificationList() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getStaleLeads();
      setNotifications(response.data.data.notifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
      alert('Failed to load notifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadNotifications();
  };

  const handleAcknowledge = async (leadId) => {
    try {
      await notificationService.acknowledge(leadId);
      setNotifications(prev => prev.filter(n => n.lead_id !== leadId));
      alert('Notification acknowledged');
    } catch (error) {
      console.error('Error acknowledging notification:', error);
      alert('Failed to acknowledge notification');
    }
  };

  const handleDismissAll = async () => {
    if (!window.confirm('Are you sure you want to dismiss all notifications?')) {
      return;
    }
    
    try {
      await notificationService.dismissAll();
      setNotifications([]);
      alert('All notifications dismissed');
    } catch (error) {
      console.error('Error dismissing notifications:', error);
      alert('Failed to dismiss notifications');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSeverityColor = (days) => {
    if (days >= 60) return 'text-red-400 bg-red-500/20 border-red-500/30';
    if (days >= 45) return 'text-orange-400 bg-orange-500/20 border-orange-500/30';
    return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
  };

  if (loading && !refreshing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-400 text-lg">Loading notifications...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />
      
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <Bell className="w-8 h-8 text-red-400" />
              Notifications
            </h1>
            <p className="text-slate-400">
              {notifications.length} lead{notifications.length !== 1 ? 's' : ''} requiring attention
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {notifications.length > 0 && (
              <button
                onClick={handleDismissAll}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
              >
                <CheckCheck className="w-4 h-4" />
                Dismiss All
              </button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        {notifications.length > 0 ? (
          <div className="space-y-4">
            {notifications.map((notification) => (
              <div
                key={notification.lead_id}
                className="bg-slate-900 rounded-xl border border-slate-800 p-6 hover:border-slate-700 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`p-3 rounded-lg border-2 ${getSeverityColor(notification.days_overdue)}`}>
                        <AlertTriangle className="w-6 h-6" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-white">{notification.organisation_name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${getSeverityColor(notification.days_overdue)}`}>
                            {notification.days_overdue} days overdue
                          </span>
                        </div>
                        
                        {notification.project_name && (
                          <p className="text-slate-400 text-sm mb-3">{notification.project_name}</p>
                        )}
                        
                        {/* Details Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-slate-500" />
                            <div>
                              <p className="text-xs text-slate-500">Client</p>
                              <p className="text-sm text-slate-300">{notification.client_name || 'N/A'}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-500" />
                            <div>
                              <p className="text-xs text-slate-500">Consultant</p>
                              <p className="text-sm text-slate-300">{notification.consultant_name || 'Unassigned'}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-500" />
                            <div>
                              <p className="text-xs text-slate-500">Last Follow-up</p>
                              <p className="text-sm text-slate-300">
                                {formatDate(notification.next_followup_date || notification.date_contacted)}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4"></div>
                            <div>
                              <p className="text-xs text-slate-500">Status</p>
                              <p className="text-sm text-slate-300">{notification.status || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Message */}
                    <div className="bg-slate-800 rounded-lg p-4 mb-4">
                      <p className="text-slate-300 text-sm">
                        <span className="text-red-400 font-semibold">⚠️ Action Required:</span> This lead has not been followed up for {notification.days_overdue} days. Please review and update the lead status or schedule a follow-up.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-800">
                  <Link
                    to={`/admin/leads/edit/${notification.lead_id}`}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-semibold transition-colors"
                  >
                    View Lead
                  </Link>
                  <button
                    onClick={() => handleAcknowledge(notification.lead_id)}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    Acknowledge
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-16">
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCheck className="w-10 h-10 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">All Caught Up!</h3>
              <p className="text-slate-400 text-lg mb-2">
                No leads requiring follow-up attention
              </p>
              <p className="text-slate-500 text-sm">
                You'll be notified when leads haven't been followed up for 30+ days
              </p>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-blue-400 mb-2">About Notifications</h4>
              <p className="text-slate-300 text-sm leading-relaxed">
                You'll receive notifications for leads that haven't been followed up for <span className="font-semibold text-blue-400">30 or more days</span>. 
                These notifications help ensure no opportunities are missed and all leads receive timely attention. 
                Click "Acknowledge" to dismiss a notification once you've reviewed the lead.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}